// Pure bucketing math for user-entered custom-metric values. Values are stored
// per explicit [startDate, endDate] bucket (date-only 'YYYY-MM-DD', UTC). The
// KPI table renders one column per period bucket; this module maps stored
// values onto those buckets and derives the prior-period (YoY) bucket.
//
// Kept free of React/cube deps so it can be unit-tested directly and (later)
// reused by the CLI KPI endpoint.

import type { CalcFormat } from './calculated-metrics';
import { DEFAULT_WEEK_START_DAY, daysSinceWeekStart, type WeekStartDay } from './week';

export type BucketGranularity = 'day' | 'week' | 'month';

export interface CustomMetricValueBucket {
  /** Bucket start, 'YYYY-MM-DD' UTC. Daily entries have startDate === endDate. */
  startDate: string;
  /** Bucket end (inclusive), 'YYYY-MM-DD' UTC. */
  endDate: string;
  amount: number;
}

const MS_PER_DAY = 86_400_000;

function parseUtc(dateOnly: string): Date {
  return new Date(`${dateOnly}T00:00:00.000Z`);
}

function toDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Additive formats (currency, counts) may be summed across finer sub-buckets;
 * rates/ratios (percent, decimal) may not — a monthly rate is not the sum of
 * its weekly rates.
 */
export function isAdditiveFormat(format: CalcFormat): boolean {
  return format === 'currency' || format === 'number';
}

/** Inclusive end date of a bucket given its start + granularity. */
export function deriveBucketEnd(startDateOnly: string, granularity: BucketGranularity): string {
  if (granularity === 'day') {
    return startDateOnly;
  }
  const start = parseUtc(startDateOnly);
  if (granularity === 'week') {
    return toDateOnly(new Date(start.getTime() + 6 * MS_PER_DAY));
  }
  // month: last calendar day of the start date's month.
  return toDateOnly(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)));
}

/**
 * Prior-year bucket start for YoY, matching the KPI main query's comparison
 * windows: daily and monthly = the same calendar date one year earlier (the
 * YoY merge pairs those buckets by month+day / month, and Feb 29 rolls over to
 * Mar 1 in non-leap years), weekly = 364 days back (52·7, lands on the same
 * weekday).
 */
export function shiftComparisonStart(startDateOnly: string, granularity: BucketGranularity): string {
  const start = parseUtc(startDateOnly);
  if (granularity === 'week') {
    return toDateOnly(new Date(start.getTime() - 364 * MS_PER_DAY));
  }
  return toDateOnly(new Date(Date.UTC(start.getUTCFullYear() - 1, start.getUTCMonth(), start.getUTCDate())));
}

/**
 * Comparison-bucket start resolver for an EXPLICIT compare window (the
 * parameterized data-pipeline path; the default dashboard keeps
 * shiftComparisonStart's year-back rule). The returned fn shifts a column
 * bucket's start back by the current→comparison window offset, mirroring the
 * offset-from-window-start merge keying (getWindowComparisonKey) so
 * custom/typed metric comparisons land on the same buckets the cube's
 * comparison rows pair with:
 *   - month: whole calendar months (the window starts' month distance);
 *   - week: whole days between the two week-start-SNAPPED window starts
 *     (always a multiple of 7 — Cube's first weekly bucket snaps below a
 *     non-aligned range start, and the keying snaps its anchors the same way);
 *   - day: whole days between the window starts.
 */
export function comparisonShiftForWindows(
  granularity: BucketGranularity,
  currentWindowStart: string,
  comparisonWindowStart: string,
  weekStartDay: WeekStartDay = DEFAULT_WEEK_START_DAY,
): (bucketStart: string) => string {
  if (granularity === 'month') {
    // 1-based month index; safe pure-string parse (no Date/timezone math).
    const monthIndex = (dateOnly: string) => Number(dateOnly.slice(0, 4)) * 12 + Number(dateOnly.slice(5, 7));
    const offsetMonths = monthIndex(currentWindowStart) - monthIndex(comparisonWindowStart);
    return (bucketStart) => {
      const total = monthIndex(bucketStart) - offsetMonths;
      const year = Math.floor((total - 1) / 12);
      const month = total - year * 12;
      return `${year}-${String(month).padStart(2, '0')}-01`;
    };
  }
  const snapMs = (dateOnly: string): number => {
    const date = parseUtc(dateOnly);
    if (granularity === 'week') {
      return date.getTime() - daysSinceWeekStart(date.getUTCDay(), weekStartDay) * MS_PER_DAY;
    }
    return date.getTime();
  };
  const offsetMs = snapMs(currentWindowStart) - snapMs(comparisonWindowStart);
  return (bucketStart) => toDateOnly(new Date(parseUtc(bucketStart).getTime() - offsetMs));
}

/** How a bucket's displayed value was derived (see resolveBucketValue). */
export type BucketValueSource = 'exact' | 'rollup' | 'empty';

export interface ResolvedBucketValue {
  value: number | null;
  source: BucketValueSource;
  /**
   * For 'rollup': whether the contained finer entries tile every day of the
   * bucket (drives the inline editor's read-only rule — a bucket fully covered
   * by finer data must be edited at the finer grain, or the new coarse row
   * would shadow it). 'exact' is trivially complete; 'empty' is not.
   */
  complete: boolean;
}

/** True when the contained entries cover every day of [bucketStart, bucketEnd]. */
function coversWholeBucket(contained: CustomMetricValueBucket[], bucketStart: string, bucketEnd: string): boolean {
  const sorted = [...contained].sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
  // Sweep a day cursor across the bucket; any gap before an entry means a
  // missing day. Overlapping entries only extend the cursor, so overlaps
  // (which the API does not prevent) cannot fake completeness.
  let cursor = parseUtc(bucketStart).getTime();
  for (const value of sorted) {
    if (parseUtc(value.startDate).getTime() > cursor) {
      return false;
    }
    const nextDay = parseUtc(value.endDate).getTime() + MS_PER_DAY;
    if (nextDay > cursor) {
      cursor = nextDay;
    }
  }
  return cursor >= parseUtc(bucketEnd).getTime() + MS_PER_DAY;
}

/**
 * Resolves a metric's value for a single bucket [bucketStart, bucketEnd]:
 *   1. an exact-range match wins (a value entered at the bucket's own grain —
 *      this is the "manually entered coarse bucket takes precedence over an
 *      incomplete finer group" rule),
 *   2. else, for additive metrics, sum every value fully contained in the
 *      bucket (roll finer entries up into the coarser bucket),
 *   3. else → null (blank): a non-additive metric with no exact match, or an
 *      additive metric with no contained values.
 * Returns null rather than 0 so empty buckets render blank and are skipped in
 * YoY/export, matching how cube-derived null cells behave. The source/complete
 * provenance feeds the KPI table's inline editor.
 */
export function resolveBucketValue(
  values: CustomMetricValueBucket[],
  bucketStart: string,
  bucketEnd: string,
  additive: boolean,
): ResolvedBucketValue {
  const contained: CustomMetricValueBucket[] = [];
  let containedSum = 0;
  for (const value of values) {
    if (value.startDate === bucketStart && value.endDate === bucketEnd) {
      // Exact-match precedence; unique per (metric, range).
      return { value: value.amount, source: 'exact', complete: true };
    }
    // Date-only 'YYYY-MM-DD' strings compare lexicographically === chronologically.
    if (additive && value.startDate >= bucketStart && value.endDate <= bucketEnd) {
      containedSum += value.amount;
      contained.push(value);
    }
  }
  if (contained.length === 0) {
    return { value: null, source: 'empty', complete: false };
  }
  return {
    value: containedSum,
    source: 'rollup',
    complete: coversWholeBucket(contained, bucketStart, bucketEnd),
  };
}

/** Value-only view of resolveBucketValue (the original API). */
export function bucketValue(values: CustomMetricValueBucket[], bucketStart: string, bucketEnd: string, additive: boolean): number | null {
  return resolveBucketValue(values, bucketStart, bucketEnd, additive).value;
}
